import * as React from 'react';
import { Check, ChevronsUpDown, Star } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import type { CostCentre } from '@/types/timesheet';

interface CostCentreComboboxProps {
  costCentres: CostCentre[];
  value?: string;
  onChange: (costCentreId: string) => void;
  onToggleFavourite?: (costCentreId: string) => void;
  disabled?: boolean;
}

export function CostCentreCombobox({ 
  costCentres, 
  value, 
  onChange, 
  onToggleFavourite,
  disabled 
}: CostCentreComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [showAll, setShowAll] = React.useState(false);

  const selectedCostCentre = costCentres.find(cc => cc.id === value);

  // Sort cost centres: favourites first, then by last used, then alphabetically
  const sortedCostCentres = React.useMemo(() => {
    return [...costCentres].sort((a, b) => {
      if (a.isFavourite && !b.isFavourite) return -1;
      if (!a.isFavourite && b.isFavourite) return 1;
      if (a.lastUsed && b.lastUsed) {
        return b.lastUsed.getTime() - a.lastUsed.getTime();
      }
      if (a.lastUsed && !b.lastUsed) return -1;
      if (!a.lastUsed && b.lastUsed) return 1;
      return a.code.localeCompare(b.code);
    });
  }, [costCentres]);

  // Fuzzy search implementation
  const filteredCostCentres = React.useMemo(() => {
    let filtered = sortedCostCentres;

    if (search) {
      const searchLower = search.toLowerCase();
      filtered = sortedCostCentres.filter(costCentre => {
        const nameMatch = costCentre.name.toLowerCase().includes(searchLower);
        const codeMatch = costCentre.code.toLowerCase().includes(searchLower);
        return nameMatch || codeMatch;
      });
    }

    // Show first 10 items unless "Show More" is clicked or searching
    if (!showAll && !search) {
      return filtered.slice(0, 10);
    }

    return filtered;
  }, [sortedCostCentres, search, showAll]);

  const hasMoreItems = !search && sortedCostCentres.length > 10 && !showAll;

  const handleToggleFavourite = (e: React.MouseEvent, costCentreId: string) => {
    e.stopPropagation();
    onToggleFavourite?.(costCentreId);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
          disabled={disabled}
        >
          {selectedCostCentre ? (
            <div className="flex items-center gap-2 truncate">
              {selectedCostCentre.isFavourite && <Star className="h-3 w-3 fill-yellow-400 text-yellow-400 shrink-0" />}
              <span className="truncate">
                <span className="font-medium">{selectedCostCentre.name}</span>
                <span className="text-xs text-muted-foreground ml-2">({selectedCostCentre.code})</span>
              </span>
            </div>
          ) : (
            "Select cost centre..."
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput 
            placeholder="Search cost centres..." 
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>No cost centre found.</CommandEmpty>
            <CommandGroup>
              {filteredCostCentres.map((costCentre) => (
                <CommandItem
                  key={costCentre.id}
                  value={costCentre.id}
                  onSelect={() => {
                    onChange(costCentre.id);
                    setOpen(false);
                  }}
                  className="flex items-center gap-2"
                >
                  <Check
                    className={cn(
                      "h-4 w-4",
                      value === costCentre.id ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {onToggleFavourite && (
                    <button
                      onClick={(e) => handleToggleFavourite(e, costCentre.id)}
                      className="shrink-0"
                    >
                      <Star 
                        className={cn(
                          "h-3 w-3",
                          costCentre.isFavourite 
                            ? "fill-yellow-400 text-yellow-400" 
                            : "text-muted-foreground hover:text-yellow-400"
                        )}
                      />
                    </button>
                  )}
                  <div className="flex-1 flex items-center gap-2">
                    <span className="font-mono text-xs">{costCentre.code}</span>
                    <span className="truncate">{costCentre.name}</span>
                  </div>
                </CommandItem>
              ))}
              {hasMoreItems && (
                <CommandItem
                  onSelect={() => setShowAll(true)}
                  className="justify-center text-sm text-muted-foreground hover:text-foreground"
                >
                  Show More ({sortedCostCentres.length - 10} more)
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
